import React, { FC, useState, useRef, useEffect, ReactNode } from 'react';
import ChevronIcon from '../icons/Chevron';
import { twMerge } from 'tailwind-merge';

type CollapseProps = {
  label: string | JSX.Element;
  children: ReactNode;
  onClick?: () => void;
  open?: boolean | string;
  notClickable?: boolean;
  hideChevron?: boolean;
  notUseInnerState?: boolean;
  resize?: number | string;
  noAnimation?: boolean;
  noRender?: boolean;
  styles?: {
    container?: string;
    header?: string;
    content?: string;
    icon?: string;
    iconBtn?: string;
  };
};

export const Collapse: FC<CollapseProps> = ({
  label,
  children,
  notUseInnerState,
  hideChevron,
  notClickable,
  styles,
  noAnimation,
  noRender,
  open,
  onClick,
  resize,
}) => {
  const [isOpen, setIsOpen] = useState(!!open);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (noAnimation) {
      return;
    }
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.style.maxHeight = isOpen
          ? `${contentRef.current.scrollHeight}px`
          : '0px';
      }
    }, 0);
  }, [isOpen, resize, noAnimation]);

  useEffect(() => {
    setIsOpen(!!open);
  }, [open]);

  const toggleCollapse = () => {
    if (notClickable) {
      return;
    }

    if (!notUseInnerState) {
      setIsOpen(!isOpen);
    }
    onClick && onClick();
  };

  return (
    <div className={styles?.container}>
      <div
        className={twMerge(
          'flex  items-center justify-between',
          !notClickable && 'cursor-pointer',
          styles?.header
        )}
        onClick={toggleCollapse}
      >
        {label}
        {!hideChevron && !notClickable && (
          <ChevronIcon
            className={twMerge('h-3 w-3', styles?.icon)}
            classNameButton={twMerge('h-3 w-3', styles?.iconBtn)}
            dir={isOpen ? 'up' : 'down'}
          />
        )}
      </div>
      <div
        className={twMerge(
          'transition-max-height overflow-hidden duration-300 ease-in-out',
          styles?.content
        )}
        ref={contentRef}
      >
        {!noRender ? children : isOpen && children}
      </div>
    </div>
  );
};

export default Collapse;
